import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'theme';

// Mirrors the inline script in public/index.html, which applies the same
// choice before first paint so the page never flashes the wrong theme.
export const resolveTheme = () => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored === 'dark' || stored === 'light') return stored;
    } catch (e) {
        // localStorage can throw in private mode; fall through to the OS setting.
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

export default function useTheme() {
    const [theme, setTheme] = useState(resolveTheme);

    useEffect(() => {
        document.documentElement.classList.toggle('dark', theme === 'dark');
        try {
            localStorage.setItem(STORAGE_KEY, theme);
        } catch (e) {
            // Not persisting is acceptable; the toggle still works for this visit.
        }
    }, [theme]);

    // Follow the OS until the visitor makes an explicit choice.
    useEffect(() => {
        const media = window.matchMedia('(prefers-color-scheme: dark)');
        const onChange = (e) => {
            if (!localStorage.getItem(STORAGE_KEY)) setTheme(e.matches ? 'dark' : 'light');
        };
        media.addEventListener('change', onChange);
        return () => media.removeEventListener('change', onChange);
    }, []);

    const toggleTheme = useCallback(() => {
        setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'));
    }, []);

    return { theme, toggleTheme };
}
